export const PULL_REFRESH_START_EDGE_PX = 64;
export const PULL_REFRESH_MIN_DISTANCE_PX = 120;
export const PULL_REFRESH_MAX_HORIZONTAL_DRIFT_PX = 40;
export const PULL_REFRESH_MIN_DURATION_MS = 180;

const INTERACTIVE_SELECTOR = [
    "a",
    "button",
    "input",
    "select",
    "textarea",
    "summary",
    "[role='button']",
    "[role='link']",
    "[role='menuitem']",
    "[role='option']",
    "[contenteditable='true']",
    "[data-no-pull-refresh]",
].join(",");

export interface PullRefreshGesture {
    startX: number;
    startY: number;
    startTime: number;
    armed: boolean;
}

export const getDocumentScrollTop = (): number => {
    if (typeof document === "undefined") return 0;
    return document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
};

export const isScrollableElement = (element: Element): boolean => {
    if (!(element instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(element);
    const canScrollY = /(auto|scroll)/.test(style.overflowY);
    return canScrollY && element.scrollHeight > element.clientHeight;
};

export const hasScrollableAncestor = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) return false;

    let element: Element | null = target;
    while (element && element !== document.body && element !== document.documentElement) {
        if (isScrollableElement(element)) return true;
        element = element.parentElement;
    }

    return false;
};

export const shouldIgnorePullRefreshTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest(INTERACTIVE_SELECTOR)) || hasScrollableAncestor(target);
};

export const canStartPullRefresh = (
    event: TouchEvent,
    isMobileLike: boolean,
    scrollTop = getDocumentScrollTop()
): boolean => {
    if (!isMobileLike || event.touches.length !== 1 || scrollTop > 0) return false;
    const touch = event.touches[0];
    if (!touch || touch.clientY > PULL_REFRESH_START_EDGE_PX) return false;
    return !shouldIgnorePullRefreshTarget(event.target);
};

export const shouldTriggerPullRefresh = (
    gesture: PullRefreshGesture,
    event: TouchEvent,
    scrollTop = getDocumentScrollTop(),
    now = Date.now()
): boolean => {
    if (!gesture.armed || event.touches.length !== 1 || scrollTop > 0) return false;

    const touch = event.touches[0];
    const distanceY = touch.clientY - gesture.startY;
    const distanceX = Math.abs(touch.clientX - gesture.startX);
    const elapsed = now - gesture.startTime;

    return (
        distanceY >= PULL_REFRESH_MIN_DISTANCE_PX &&
        distanceX <= PULL_REFRESH_MAX_HORIZONTAL_DRIFT_PX &&
        elapsed >= PULL_REFRESH_MIN_DURATION_MS
    );
};
